import type { ICol } from "../../../../interfaces";
import { StyledSpaceCell, StyledSplitTh, StyledTh } from "../../../../styled";
import { v4 as uuidv4 } from "uuid";
import { getCellWidth } from "../../../utils/getCellWidth";
import type { CSSProperties } from "react";
import CISRHeader from "../../../CISRHeader/CISRHeader";
import TriangleHeader from "../../../../components/shared/TriangleCells/TriangleHeader/TriangleHeader";
import HeaderCell from "../../../../components/shared/EntriesGenerator/HeaderCell";
import { useShowContent } from "../../../hooks/useShowContent";
import { IconPlus, IconX } from "@tabler/icons-react";
import { ActionIcon, Flex } from "@mantine/core";
import { useDispatch, useSelector } from "../../../../redux/hooks";
import {
  addDynamicColumn,
  removeDynamicColumn,
} from "../../../../redux/slices/generatorSlice";
import { setEditing } from "../../../../redux/slices/entrySlice";
import { useCanEditEntry } from "../../../hooks/useCanEditEntry";
import AuthorizationModal from "../../../_Modals/AuthorizationModal/AuthorizationModal";
import type { ICredentials } from "../../../../interfaces";
import { useDisclosure } from "@mantine/hooks";
import { useParams } from "react-router-dom";
import { useIsOnline, usePostApi } from "../../../../hooks";
import { getYachtId } from "../../../../utils";

interface Props {
  headerObj: ICol;
  theme: any;
  headerSize?: any;
  colspan?: number;
  rowspan?: number;
  summary?: boolean;
  sectionIndex: number;
  entryIndex?: number;
  columnIndex?: number;
  headerIndex?: number;
  headersLength?: number;
  pdfEntry?: boolean;
}

const SingleHeader = ({
  headerObj,
  theme,
  headerSize = "",
  colspan = 1,
  rowspan = 1,
  summary,
  sectionIndex,
  entryIndex,
  columnIndex,
  headerIndex,
  headersLength,
  pdfEntry,
}: Props) => {
  const { shouldHideElement } = useShowContent();
  const dispatch = useDispatch();
  const canEditEntry = useCanEditEntry();
  const [opened, { open, close }] = useDisclosure(false);
  const bridgeLogUsers = useSelector(
    state => state.generatorSlice,
  ).bridgeLogUsers;

  const sections = useSelector(state => state.generatorSlice.lbentry.sections);
  const { logbookId, lbeidOrPartId } = useParams();
  const isOnline = useIsOnline();
  const { post } = usePostApi();

  const recordHistory = async (action: string) => {
    if (!isOnline) return;
    await post({
      request: "record-history-action",
      body: {
        params: {
          yachtid: getYachtId(),
          lbid: logbookId,
          lbeid: lbeidOrPartId,
          action,
        },
      },
      shouldRunAfterAutosave: true,
    });
  };

  const getDynamicColumnCount = () => {
    if (!headerObj.dynamicCols || !sections[sectionIndex]) return 0;

    return sections[sectionIndex].headers.filter(
      h => h.dynamicCols && h.name === headerObj.name,
    ).length;
  };

  const showDeleteButton = getDynamicColumnCount() > 1;

  const handleAddColumn = () => {
    if (columnIndex !== undefined && headerIndex !== undefined) {
      dispatch(setEditing(true));
      dispatch(
        addDynamicColumn({
          sectionIndex,
          columnIndex,
          headerIndex,
        }),
      );
    }
  };

  const handleRemoveColumn = async () => {
    if (columnIndex !== undefined && headerIndex !== undefined) {
      dispatch(setEditing(true));
      dispatch(
        removeDynamicColumn({
          sectionIndex,
          columnIndex,
          headerIndex,
        }),
      );

      await recordHistory(`User Deleted Column In Entry`);

      close();
    }
  };

  const handleDeleteClick = () => {
    open();
  };

  const shouldHide = shouldHideElement(headerObj, entryIndex);
  if (shouldHide) return null;
  if (headerObj.hidden) {
    return (
      <StyledSpaceCell
        colSpan={colspan}
        rowSpan={rowspan}
        key={uuidv4()}
        style={{
          height: "0px",
          margin: "0px",
          pointerEvents: "none",
          padding: "0px",
        }}
      />
    );
  }

  if (
    headerObj.type === "split" &&
    headerObj.name === "" &&
    (headerIndex !== undefined
      ? headerIndex === 0 && headersLength === 1
      : true)
  ) {
    return (
      <StyledSpaceCell
        colSpan={colspan}
        rowSpan={rowspan}
        key={uuidv4()}
        style={{
          height: "0px",
          margin: "0px",
          pointerEvents: "none",
          padding: "0px",
        }}
      />
    );
  }

  if (
    headerObj.type === "col" &&
    (headerObj.name === "" || !("name" in headerObj)) &&
    headerObj.id === "entry-date"
  ) {
    return (
      <StyledSpaceCell
        colSpan={colspan}
        rowSpan={rowspan}
        key={uuidv4()}
        style={{
          height: "0px",
          margin: "0px",
          pointerEvents: "none",
          padding: "0px",
        }}
      />
    );
  }

  if (headerObj.type === "space") {
    return (
      <StyledSpaceCell
        key={uuidv4()}
        rowSpan={rowspan}
        style={{
          width: getCellWidth(headerObj.width),
        }}
      />
    );
  }
  const style: CSSProperties = {
    width: getCellWidth(headerObj.width),
    border: `1px solid ${theme.colors.thBorder[0]}`,
    minWidth:
      headerObj.NMEA === "BARON" ||
      headerObj.NMEA === "WATER TEMP" ||
      headerObj.NMEA === "AIR TEMP"
        ? "102px"
        : headerObj.NMEA === "WIND FORCE" || headerObj.NMEA === "WIND DIR"
          ? "75px"
          : "",
  };

  if (headerObj.unit === "boolna") style.background = "#C92A2A";
  if (headerObj.unit === "cisr_header")
    style.background = theme.colors.background[0];

  // Handle triangle headers with the new dynamic component
  if (headerObj.unit === "triangle" && headerObj.triangleHeaders) {
    const leftText = headerObj.triangleHeaders[0] || "";
    const rightText = headerObj.triangleHeaders[1] || "";

    if (headerSize !== "lb-headers-none") {
      return (
        <TriangleHeader
          key={uuidv4()}
          leftText={leftText}
          rightText={rightText}
          colSpan={colspan}
          rowSpan={rowspan}
          width={getCellWidth(headerObj.width)}
        />
      );
    }
  }

  if (headerSize !== "lb-headers-none") {
    if (summary)
      return (
        <HeaderCell
          col={headerObj}
          sIndex={sectionIndex}
          colspan={colspan}
          rowspan={rowspan}
          hasChildren={!!headerObj.cols}
          thStyling
          pdfEntry={pdfEntry}
        />
      );

    const title = [];
    if (headerObj.name && headerObj.name.indexOf("<br>") > -1) {
      const arr = headerObj.name.split("<br>");
      arr.forEach((v: string) =>
        title.push(<StyledSplitTh key={uuidv4()}>{v}</StyledSplitTh>),
      );
    } else {
      title.push(headerObj.name);
    }
    return (
      <>
        <StyledTh
          colSpan={colspan}
          rowSpan={rowspan}
          key={uuidv4()}
          style={style}>
          <Flex
            direction={headerObj.dynamicCols ? "row" : "column"}
            justify={headerObj.dynamicCols ? "space-between" : "center"}
            wrap="wrap"
            gap={8}
            align="center">
            <div>
              {headerObj.unit === "cisr_header" ? <CISRHeader /> : title}
              {headerObj.displayValue && (
                <p style={{ fontWeight: 200, fontSize: 18 }}>
                  {headerObj.displayValue}
                </p>
              )}
            </div>
            {canEditEntry && headerObj.dynamicCols && (
              <Flex gap={4}>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="blue"
                  onClick={handleAddColumn}
                  title="Add Column">
                  <IconPlus size={18} />
                </ActionIcon>
                {showDeleteButton && (
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={handleDeleteClick}
                    title="Delete Column">
                    <IconX size={18} />
                  </ActionIcon>
                )}
              </Flex>
            )}
          </Flex>
        </StyledTh>

        <AuthorizationModal
          authorizedUserTypes={[7]}
          nonAuthorizedMessage={
            <span>
              You are not permitted to delete columns, select a{" "}
              <span style={{ fontWeight: 200 }}>Yacht Admin</span> to approve
              this on your behalf.
            </span>
          }
          opened={opened}
          close={close}
          onSubmit={handleRemoveColumn}
          loading={!bridgeLogUsers}
          users={bridgeLogUsers}
          isEntry={false}
        />
      </>
    );
  } else {
    return <></>;
  }
};

export default SingleHeader;
